// The agent page's chat: talk to this one agent (on a host with a chat, its turn pinned to the agent) — its answer, and
// under it in plain words what it used, whether its guardrails passed it, the time and tokens. Its tests' questions
// start a conversation in one click. Every turn is a run, so the runs list refreshes after it. The turn goes
// through the host's chat port (`AgentsHost.chat`; without one, the SDK's own ask route).
import { cn } from '@cortex/ui';
import { Check, Loader2, MessageSquare, RotateCcw, Send, ShieldAlert, Square, XCircle } from 'lucide-react';
import * as React from 'react';
import { askChat, useAgentsHost } from './host.tsx';
import { applyEvent, formatUsage, startTurn, stopTurn, type Item } from './transcript.ts';
import { btnGhost, btnPrimary, input } from './ui.tsx';
import { OutcomeBadge } from './RecentRuns.tsx';

interface Verdict {
  verdict?: string;
  reason?: string;
  checks?: { name: string; ok: boolean; detail?: string }[];
}

export function TestChat({
  agentKey,
  title,
  onTurn,
  suggestions = [],
  footer,
}: {
  agentKey: string;
  title: string;
  onTurn?: () => void;
  /** Questions to start with (its tests'), one click each. */
  suggestions?: string[];
  /** A small line under the input (the host page: the model, what it runs). */
  footer?: React.ReactNode;
}) {
  const [items, setItems] = React.useState<Item[]>([]);
  const [verdicts, setVerdicts] = React.useState<Record<string, Verdict | null>>({});
  /** How long each answer took, by its id. */
  const [took, setTook] = React.useState<Record<string, number>>({});
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const abort = React.useRef<AbortController | null>(null);
  const end = React.useRef<HTMLDivElement>(null);
  const persona = `agent-${agentKey}`;
  const host = useAgentsHost();
  const { Markdown } = host;
  const chat = React.useMemo(() => host.chat ?? askChat(host.client), [host]);

  React.useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'nearest' });
  }, [items]);

  /** Answered approvals by the answer they belong to: approved (sent again) or declined. */
  const [decided, setDecided] = React.useState<Record<string, 'approved' | 'declined'>>({});

  const send = async (again?: { text: string; approvals?: string[] }) => {
    const text = again ? again.text : draft.trim();
    if (!text || busy) return;
    if (!again) setDraft('');
    setBusy(true);
    let answerId = '';
    setItems((it) => {
      const next = startTurn(it, text);
      answerId = next.at(-1)!.id;
      return next;
    });
    const ac = new AbortController();
    abort.current = ac;
    const started = Date.now();
    try {
      for await (const e of chat.turn({
        agent: agentKey,
        message: text,
        sessionId,
        signal: ac.signal,
        ...(again?.approvals ? { approvals: again.approvals } : {}),
      })) {
        if (e.type === 'session') setSessionId(e.sessionId);
        if (e.type === 'run_end' && e.agent === persona)
          setVerdicts((v) => ({ ...v, [answerId]: (e.guardrail as Verdict | null) ?? null }));
        setItems((it) => applyEvent(it, e));
      }
      setItems((it) => stopTurn(it));
    } catch (e) {
      if (ac.signal.aborted) setItems((it) => stopTurn(it));
      else
        setItems((it) =>
          applyEvent(it, {
            type: 'error',
            message: `The assistant is not reachable: ${(e as Error).message}`,
            status: 0,
          }),
        );
    } finally {
      const id = answerId;
      setTook((t) => ({ ...t, [id]: Date.now() - started }));
      abort.current = null;
      setBusy(false);
      onTurn?.();
    }
  };

  const reset = () => {
    setItems([]);
    setVerdicts({});
    setTook({});
    setDecided({});
    setSessionId(null);
  };

  return (
    <div data-testid="agent-test-chat" className="flex flex-col gap-3">
      <div
        data-testid="agent-chat-transcript"
        className="max-h-[420px] min-h-[120px] overflow-y-auto rounded-md border border-card-border bg-page-bg p-3 flex flex-col gap-3"
      >
        {!items.length ? (
          <div className="text-sm text-page-muted flex flex-col gap-2" data-testid="agent-chat-empty">
            <span>
              <MessageSquare size={14} className="inline mr-1 opacity-60" />
              {suggestions.length
                ? 'Ask anything, or start with a question from its tests:'
                : `Ask ${title} anything.`}
            </span>
            {suggestions.length ? (
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((q) => (
                  <button
                    key={q}
                    type="button"
                    data-testid="agent-chat-suggestion"
                    className="rounded-full border border-card-border bg-transparent px-2.5 py-0.5 text-xs text-page-text cursor-pointer hover:border-nav-active text-left"
                    title={q}
                    onClick={() => void send({ text: q })}
                  >
                    {q.length > 70 ? `${q.slice(0, 68)}…` : q}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {items.map((it) => {
          if (it.kind === 'user')
            return (
              <div
                key={it.id}
                data-testid="agent-chat-user"
                className="self-end max-w-[85%] rounded-lg bg-nav-active text-white text-sm px-3 py-2 whitespace-pre-wrap"
              >
                {it.text}
              </div>
            );
          if (it.kind === 'error')
            return (
              <div key={it.id} data-testid="agent-chat-error" className="text-sm text-red-600">
                {it.text}
              </div>
            );
          const v = verdicts[it.id];
          return (
            <div
              key={it.id}
              data-testid="agent-chat-answer"
              data-complete={it.complete}
              data-status={it.status ?? ''}
              className="max-w-[95%] flex flex-col gap-2"
            >
              {it.approval ? (
                <div
                  className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm flex flex-col gap-2"
                  data-testid="agent-chat-approval"
                  data-tool={it.approval.tool}
                >
                  <div>
                    {title} wants to run <span className="font-mono">{it.approval.tool}</span>
                    {Object.keys(it.approval.input).length ? ' with:' : '.'}
                  </div>
                  {Object.keys(it.approval.input).length ? (
                    <pre className="m-0 text-xs font-mono whitespace-pre-wrap break-all">
                      {JSON.stringify(it.approval.input, null, 2)}
                    </pre>
                  ) : null}
                  {decided[it.id] ? (
                    <div className="text-xs text-page-muted" data-testid="agent-chat-approval-decided">
                      {decided[it.id] === 'approved'
                        ? 'You approved this call.'
                        : 'You declined; the call did not run.'}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className={btnPrimary}
                        data-testid="agent-chat-approve"
                        disabled={busy}
                        onClick={() => {
                          setDecided((d) => ({ ...d, [it.id]: 'approved' }));
                          void send({ text: it.prompt ?? '', approvals: [it.approval!.key] });
                        }}
                      >
                        <Check size={14} /> Approve and run
                      </button>
                      <button
                        type="button"
                        className={btnGhost}
                        data-testid="agent-chat-decline"
                        disabled={busy}
                        onClick={() => setDecided((d) => ({ ...d, [it.id]: 'declined' }))}
                      >
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              ) : it.text ? (
                <div
                  className="rounded-lg border border-card-border bg-card-bg px-3 py-2 text-sm"
                  data-testid="agent-chat-text"
                >
                  <Markdown source={it.text} />
                </div>
              ) : !it.complete ? (
                <Loader2 className="h-4 w-4 animate-spin text-page-muted" />
              ) : null}
              {it.tools.length ? (
                <div className="flex flex-wrap items-center gap-1 text-[12px] text-page-muted">
                  Used:
                  {it.tools.map((t, i) => (
                    <span
                      key={t.id}
                      data-testid="agent-chat-tool"
                      data-tool={t.name}
                      data-status={t.status}
                      title={`${t.name}${t.summary ? ` — ${t.summary}` : ''}`}
                      className={cn(
                        'inline-flex items-center gap-1',
                        t.status === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-page-text',
                      )}
                    >
                      {t.status === 'running' ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                      {t.status === 'failed' ? <XCircle className="h-3 w-3" /> : null}
                      {t.name.replace(/_/g, ' ')}
                      {t.status === 'failed' ? ' (failed)' : ''}
                      {i < it.tools.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-2 text-[12px] text-page-muted">
                {v?.verdict ? (
                  <span
                    data-testid="agent-chat-guardrail"
                    data-verdict={v.verdict}
                    title={
                      v.reason || (v.checks ?? []).map((c) => `${c.ok ? '✓' : '✗'} ${c.name}`).join(', ')
                    }
                    className={cn(
                      'inline-flex items-center gap-1',
                      v.verdict === 'pass'
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-amber-700 dark:text-amber-400',
                    )}
                  >
                    {v.verdict === 'pass' ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <ShieldAlert className="h-3 w-3" />
                    )}
                    {v.verdict === 'pass'
                      ? 'Checked by its guardrails'
                      : it.status === 'refused'
                        ? 'Refused by its guardrails'
                        : 'Not verified by its guardrails'}
                  </span>
                ) : null}
                {it.outcome ? (
                  <OutcomeBadge outcome={it.outcome} stop={it.stop ?? null} testId="agent-chat-outcome" />
                ) : null}
                {took[it.id] !== undefined ? (
                  <span>{Math.max(1, Math.round(took[it.id]! / 1000))} s</span>
                ) : null}
                {it.usage ? <span data-testid="agent-chat-usage">{formatUsage(it.usage)}</span> : null}
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <div className="flex gap-2 items-end">
        <textarea
          className={input + ' min-h-[40px] max-h-40'}
          data-testid="agent-chat-input"
          placeholder={`Ask ${title}…`}
          value={draft}
          rows={2}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {busy ? (
          <button
            type="button"
            className={btnGhost}
            data-testid="agent-chat-stop"
            onClick={() => abort.current?.abort()}
          >
            <Square size={14} /> Stop
          </button>
        ) : (
          <button
            type="button"
            className={btnPrimary}
            data-testid="agent-chat-send"
            disabled={!draft.trim()}
            onClick={() => void send()}
          >
            <Send size={14} /> Send
          </button>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 text-[11.5px] text-page-muted">
        <span>{footer}</span>
        <button
          type="button"
          className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-[11.5px] text-nav-active cursor-pointer disabled:opacity-40 disabled:cursor-default"
          data-testid="agent-chat-reset"
          disabled={busy || !items.length}
          onClick={reset}
        >
          <RotateCcw size={12} /> New conversation
        </button>
      </div>
    </div>
  );
}
